// src/hooks/useAuth.ts
//
// GIDC (consumer) auth for the storefront - D-033. Firebase Auth issues the token
// (this tenant's own territory project, see firebaseConfig); SmartShop's backend
// verifies it and returns the account.
//
// Two backend calls, deliberately different:
//   smartshop-account-sync  - LOGIN MOMENT only. Writes: binds uid to account,
//                             mints the GID through SmartCom on first registration,
//                             issues the consumer grant. Needs terms_accepted on
//                             a brand-new account (D-079).
//   smartshop-gidc-me       - EVERY OTHER TIME. Pure gated read, no writes.
//
// gido_id is sent on both (D-035 multi-tenant scoping). It is input hygiene only -
// the real gate is resolve_consumer against consumer_grants server-side.
//
// House rule: short hyphen "-" only.
import { useState, useEffect, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import { auth, SMARTSHOP_API, CLIENT_ID } from '../firebaseConfig';
import type { CartItem } from './useCart';

export interface GidcAccount {
  account_id: string;
  gid: string;
  email: string;
  name: string | null;
  last_login_at: string | null;
  consents?: Record<string, any>;
  orders: any[];
  saved_cart: {
    cart_id: string;
    item_count: number;
    total: number;
    items: CartItem[];
  } | null;
}

function friendly(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use': return 'That email already has an account. Try signing in.';
    case 'auth/invalid-email':        return 'That email address does not look right.';
    case 'auth/weak-password':        return 'Please choose a password of at least 6 characters.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':       return 'Email or password not recognised.';
    case 'auth/too-many-requests':    return 'Too many attempts. Please wait a moment and try again.';
    default:                          return 'Something went wrong. Please try again.';
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [account, setAccount] = useState<GidcAccount | null>(null);
  const [checking, setChecking] = useState(true);   // still restoring the session
  const [busy, setBusy] = useState(false);          // a sign-in/up call is running
  const [error, setError] = useState<string | null>(null);

  // --- the gated read (every page load while signed in) ---
  const loadAccount = useCallback(async (u: User) => {
    const token = await u.getIdToken();
    const res = await fetch(`${SMARTSHOP_API}/smartshop-gidc-me`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ gido_id: CLIENT_ID }),
    });
    if (!res.ok) throw new Error(String(res.status));
    const data: GidcAccount = await res.json();
    setAccount(data);
    return data;
  }, []);

  // --- the login moment (registration / sign-in only) ---
  const syncAccount = useCallback(async (u: User, opts: { name?: string; termsAccepted?: boolean }) => {
    const token = await u.getIdToken();
    const res = await fetch(`${SMARTSHOP_API}/smartshop-account-sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        gido_id: CLIENT_ID,
        name: opts.name,
        terms_accepted: !!opts.termsAccepted,
        // the guest cart, so the login moment binds it to this account
        // (backend: bind_and_merge_on_login). Null when there is no guest cart.
        cart_id: localStorage.getItem('smartshop_cart_id'),
        session_id: localStorage.getItem('smartshop_session_id'),
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || String(res.status));
    }
    const data: GidcAccount = await res.json();
    setAccount(data);
    return data;
  }, []);

  // restore an existing session on mount; Firebase persists it across reloads
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try { await loadAccount(u); } catch { setAccount(null); }
      } else {
        setAccount(null);
      }
      setChecking(false);
    });
    return unsub;
  }, [loadAccount]);

  const register = useCallback(async (email: string, password: string, name: string, termsAccepted: boolean) => {
    setBusy(true); setError(null);
    try {
      if (!termsAccepted) throw new Error('Please accept the terms to create an account.');
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await syncAccount(cred.user, { name: name.trim(), termsAccepted: true });
      return true;
    } catch (e: any) {
      setError(e?.code ? friendly(e.code) : (e?.message || 'Could not create the account.'));
      return false;
    } finally { setBusy(false); }
  }, [syncAccount]);

  const signIn = useCallback(async (email: string, password: string) => {
    setBusy(true); setError(null);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      // existing account -> terms already on file; sync only touches last_login_at
      await syncAccount(cred.user, {});
      return true;
    } catch (e: any) {
      setError(e?.code ? friendly(e.code) : (e?.message || 'Could not sign in.'));
      return false;
    } finally { setBusy(false); }
  }, [syncAccount]);

  const resetPassword = useCallback(async (email: string) => {
    setBusy(true); setError(null);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      return true;
    } catch (e: any) {
      setError(e?.code ? friendly(e.code) : 'Could not send the reset email.');
      return false;
    } finally { setBusy(false); }
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut(auth);
    setAccount(null);
  }, []);

  return {
    user, account, checking, busy, error,
    signedIn: !!user,
    register, signIn, signOut, resetPassword,
    refresh: async () => { if (user) return loadAccount(user); },
  };
}